import { z } from "zod";
import { AudiobookSchema } from "./audiobook";
import { SessionResponseSchema, SignInRequestSchema, SignOutResponseSchema } from "./auth";
import {
  BookDeleteResponseSchema,
  BookDetailSchema,
  BookListResponseSchema,
  BookPatchSchema,
} from "./book";
import { DraftReadSchema, DraftRevisionSchema, DraftSchema, DraftWriteSchema } from "./drafts";
import type { ErrorCode } from "./errors";
import { BookEstimateSchema, BookRenderRequestSchema } from "./estimate";
import { HealthResponseSchema } from "./health";
import { JobRetryRequestSchema, JobSummarySchema } from "./jobs";
import { NodesQuerySchema, NodesResponseSchema } from "./manuscript";
import {
  ChapterProposalReadySchema,
  ChapterProposalSchema,
  ConfirmRequestSchema,
  ProposalDraftSchema,
} from "./proposal";
import { QualityResolutionSchema, ReviewPolicySchema } from "./quality";
import { RenderProgressSchema, StopRequestSchema } from "./render";
import {
  ApproveRequestSchema,
  AudioQuerySchema,
  BlockPatchSchema,
  ChapterRenderRequestSchema,
  ChapterReviewSchema,
  RerunRequestSchema,
  RestoreRequestSchema,
  ReviewQuerySchema,
  VersionsResponseSchema,
} from "./review";
import { SampleRequestSchema, SampleSchema } from "./samples";
import {
  PronunciationCheckRequestSchema,
  RerunUnapprovedResponseSchema,
  StyleSheetPutSchema,
  StyleSheetSchema,
} from "./stylesheet";
import {
  VoiceImportSchema,
  VoiceLibraryQuerySchema,
  VoiceLibraryResponseSchema,
} from "./voiceLibrary";
import {
  CastingPutSchema,
  CastingSchema,
  VoiceSchema,
  VoicesQuerySchema,
  VoicesResponseSchema,
} from "./voices";
import {
  TermOccurrencesRequestSchema,
  TermOccurrencesSchema,
  WordChangeApplyRequestSchema,
  WordChangeApplyResultSchema,
  WordChangeSuggestionsSchema,
  WordChangeSuggestRequestSchema,
} from "./wordChanges";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/**
 * One API route as both sides see it. `request` is the JSON body schema
 * (absent for GET/DELETE and for multipart routes, which set `multipart`),
 * `query` the query-string schema, `response` the 2xx JSON body. A redirect
 * route (302 to a presigned URL) has no body and no response schema.
 * The response validator in dev and test and the MSW mock backend both read
 * this list; the tests assert every entry is complete.
 */
export interface RouteDef<
  Req extends z.ZodType | undefined = z.ZodType | undefined,
  Res extends z.ZodType | undefined = z.ZodType | undefined,
> {
  readonly method: HttpMethod;
  /** Path relative to /api, with :params. */
  readonly path: string;
  /** One line of what the route does, in the glossary's terms. */
  readonly description: string;
  /** True when the route is reachable without the session cookie. */
  readonly public?: boolean;
  /** The body is multipart/form-data (an upload), not JSON. */
  readonly multipart?: boolean;
  /** Answers 302 to a presigned bucket URL (10 min); no JSON body. */
  readonly redirect?: boolean;
  /** 2xx status of the success response; 200 when absent. A route may list two (samples: 200 cached, 202 rendering). */
  readonly status?: readonly (200 | 201 | 202)[];
  readonly request?: Req;
  readonly query?: z.ZodType;
  readonly response?: Res;
  /** Error codes this route answers beyond COMMON_ERROR_CODES. */
  readonly errors?: readonly ErrorCode[];
  /** What the call does to Book and Chapter state, when it does anything. */
  readonly transition?: string;
}

/** Codes any session-gated route can answer, so routes list only their own. */
export const COMMON_ERROR_CODES = [
  "UNAUTHENTICATED",
  "NOT_FOUND",
  "VALIDATION_FAILED",
  "INTERNAL",
  "SERVICE_UNAVAILABLE",
] as const satisfies readonly ErrorCode[];

export function defineRoutes<const T extends readonly RouteDef[]>(routes: T): T {
  return routes;
}

export const sessionRoutes = defineRoutes([
  {
    method: "POST",
    path: "/session",
    description: "Sign in with the Author password; sets the 30-day session cookie",
    public: true,
    request: SignInRequestSchema,
    response: SessionResponseSchema,
    errors: ["INVALID_PASSWORD", "TOO_MANY_ATTEMPTS", "PAYLOAD_TOO_LARGE"],
  },
  {
    method: "GET",
    path: "/session",
    description: "Whether the session cookie is valid",
    public: true,
    response: SessionResponseSchema,
  },
  {
    method: "DELETE",
    path: "/session",
    description: "Sign out; clears the cookie",
    public: true,
    response: SignOutResponseSchema,
  },
]);

export const healthRoutes = defineRoutes([
  {
    method: "GET",
    path: "/health",
    description: "Postgres, bucket, ffmpeg, worker heartbeat; 503 when any fails",
    public: true,
    response: HealthResponseSchema,
  },
]);

export const bookRoutes = defineRoutes([
  {
    method: "GET",
    path: "/books",
    description: "The dashboard list of Books",
    response: BookListResponseSchema,
  },
  {
    method: "POST",
    path: "/books",
    description: "Upload a Source (multipart field `file`, at most 32 MB); enqueues Parse",
    multipart: true,
    status: [201],
    response: BookDetailSchema,
    errors: ["PDF_NOT_SUPPORTED", "UNSUPPORTED_FORMAT", "PAYLOAD_TOO_LARGE"],
    transition:
      "Book: (new) -> uploaded; Parse job enqueued; parsed on success, failed after 3 attempts",
  },
  {
    method: "GET",
    path: "/books/:id",
    description: "The poll target: Book, Chapter rail, counts, jobs, summaries of every screen",
    response: BookDetailSchema,
  },
  {
    method: "PATCH",
    path: "/books/:id",
    description:
      "Title and author any time; language until confirmed; model and stability until the first Chapter Audio",
    request: BookPatchSchema,
    response: BookDetailSchema,
    errors: ["BOOK_STATE_CONFLICT", "RENDER_SETTINGS_LOCKED"],
    transition:
      "Book state unchanged; a model or stability change invalidates the Estimate fingerprint",
  },
  {
    method: "DELETE",
    path: "/books/:id",
    description: "Delete the Book and everything under its bucket prefix (cascade job)",
    response: BookDeleteResponseSchema,
    transition: "Book removed; live jobs cancelled",
  },
  {
    method: "GET",
    path: "/books/:id/source",
    description: "302 to the uploaded Source",
    redirect: true,
  },
  {
    method: "GET",
    path: "/books/:id/cover",
    description: "302 to the cover image",
    redirect: true,
  },
  {
    method: "POST",
    path: "/books/:id/cover",
    description: "Upload or replace the cover (multipart field `file`, JPEG or PNG)",
    multipart: true,
    response: BookDetailSchema,
    errors: ["UNSUPPORTED_FORMAT", "PAYLOAD_TOO_LARGE"],
    transition: "An assembled Audiobook becomes stale",
  },
  {
    method: "DELETE",
    path: "/books/:id/cover",
    description: "Remove the cover",
    response: BookDetailSchema,
    transition: "An assembled Audiobook becomes stale",
  },
]);

export const proposalRoutes = defineRoutes([
  {
    method: "GET",
    path: "/books/:id/proposal",
    description: "The Chapter Proposal with the Author's draft edits, or its pending/failed status",
    response: ChapterProposalSchema,
  },
  {
    method: "PUT",
    path: "/books/:id/proposal",
    description:
      "Replace the draft: full Chapter list (rename, merge, split, exclude) and the Calibration Chapter",
    request: ProposalDraftSchema,
    response: ChapterProposalReadySchema,
    errors: ["BOOK_STATE_CONFLICT"],
    transition: "Book stays parsed; Chapter rows replaced",
  },
  {
    method: "POST",
    path: "/books/:id/proposal/confirm",
    description: "Confirm Book facts and the Chapter list; enqueues the Style Sheet",
    request: ConfirmRequestSchema,
    response: BookDetailSchema,
    errors: ["UNAPPLIED_CHANGES", "BOOK_STATE_CONFLICT"],
    transition:
      "Book: parsed -> confirmed -> styleSheetReady -> calibrating (Calibration Chapter refining); excluded Chapters -> excluded",
  },
  {
    method: "GET",
    path: "/books/:id/nodes",
    description:
      "Manuscript nodes by ordinal range, for split points and Samples of unrefined Chapters",
    query: NodesQuerySchema,
    response: NodesResponseSchema,
  },
]);

export const styleSheetRoutes = defineRoutes([
  {
    method: "GET",
    path: "/books/:id/style-sheet",
    description:
      "The current Style Sheet with its recorded Instructions and the Pronunciation Check Voice",
    response: StyleSheetSchema,
  },
  {
    method: "PUT",
    path: "/books/:id/style-sheet",
    description:
      "Replace book facts, conventions, and terms; baseVersion guards against a concurrent change",
    request: StyleSheetPutSchema,
    response: StyleSheetSchema,
    errors: [
      "STYLE_SHEET_CONFLICT",
      "BOOK_STATE_CONFLICT",
      "PAYLOAD_TOO_LARGE",
      "CHAPTER_RENDERING",
    ],
    transition: "New Style Sheet version (origin authorEdit); Approved Chapters keep their script",
  },
  {
    method: "POST",
    path: "/books/:id/style-sheet/rerun-unapproved",
    description: "Re-run Refinement on every included Chapter that is not Approved",
    response: RerunUnapprovedResponseSchema,
    errors: ["UNAPPLIED_CHANGES", "BOOK_STATE_CONFLICT"],
    transition: "Chapters inReview -> refining -> inReview; Book -> refining",
  },
  {
    method: "POST",
    path: "/books/:id/style-sheet/terms/:termId/check",
    description:
      "Pronunciation Check: a Sample of the term in the sentence where it first occurs, through the render path",
    request: PronunciationCheckRequestSchema,
    status: [200, 202],
    response: SampleSchema,
    errors: ["STYLE_SHEET_CONFLICT", "CASTING_MISSING"],
  },
  {
    method: "POST",
    path: "/books/:id/style-sheet/occurrences",
    description:
      "Where each term (of the saved Style Sheet or an unsaved draft) is spoken with its pronunciation, per Chapter",
    request: TermOccurrencesRequestSchema,
    response: TermOccurrencesSchema,
  },
]);

export const wordChangeRoutes = defineRoutes([
  {
    method: "POST",
    path: "/books/:id/word-changes/suggestions",
    description:
      "Every other place the current scripts use a word the Author just replaced, with inflected forms a yes/no model accepts",
    request: WordChangeSuggestRequestSchema,
    response: WordChangeSuggestionsSchema,
  },
  {
    method: "POST",
    path: "/books/:id/word-changes/apply",
    description:
      "Apply accepted suggestions outside the Chapter being edited: one new script version per Chapter, and the Style Sheet entry",
    request: WordChangeApplyRequestSchema,
    response: WordChangeApplyResultSchema,
    errors: ["STYLE_SHEET_CONFLICT"],
    transition:
      "Each changed Chapter approved -> inReview; its rendered audio and the Audiobook become stale",
  },
]);

export const chapterRoutes = defineRoutes([
  {
    method: "GET",
    path: "/books/:id/chapters/:cid",
    description:
      "Review: the current (or a given) script version aligned with its Manuscript nodes",
    query: ReviewQuerySchema,
    response: ChapterReviewSchema,
  },
  {
    method: "GET",
    path: "/books/:id/chapters/:cid/versions",
    description: "Every script version of the Chapter, newest first",
    response: VersionsResponseSchema,
  },
  {
    method: "PATCH",
    path: "/books/:id/chapters/:cid/blocks/:bid",
    description:
      "Edit a Block's text; forks an edit version on the first edit of a refinement or restore version",
    request: BlockPatchSchema,
    response: ChapterReviewSchema,
    errors: ["UNAPPLIED_CHANGES", "CHAPTER_RENDERING", "CHAPTER_STATE_CONFLICT"],
    transition:
      "Chapter approved -> inReview (Book -> inReview); rendered audio and the Audiobook become stale",
  },
  {
    method: "POST",
    path: "/books/:id/chapters/:cid/approve",
    description:
      "Approve the version: freezes the Approved script; the Calibration Chapter releases the rest once",
    request: ApproveRequestSchema,
    response: ChapterReviewSchema,
    errors: [
      "UNAPPLIED_CHANGES",
      "QUALITY_REVIEW_REQUIRED",
      "CASTING_MISSING",
      "DROPPED_NODES",
      "CHAPTER_RENDERING",
      "CHAPTER_STATE_CONFLICT",
    ],
    transition:
      "Chapter inReview -> approved; Book -> approved when every included Chapter is; calibration approval: other Chapters pending -> refining (once)",
  },
  {
    method: "POST",
    path: "/books/:id/chapters/:cid/rerun",
    description: "Re-run Refinement with an optional Instruction, recorded in the Style Sheet",
    request: RerunRequestSchema,
    response: ChapterReviewSchema,
    errors: ["UNAPPLIED_CHANGES", "CHAPTER_RENDERING", "CHAPTER_STATE_CONFLICT"],
    transition: "Chapter -> refining -> inReview (new version); Book -> refining",
  },
  {
    method: "POST",
    path: "/books/:id/chapters/:cid/restore",
    description: "Make an earlier version current again (a new restore version)",
    request: RestoreRequestSchema,
    response: ChapterReviewSchema,
    errors: ["UNAPPLIED_CHANGES", "CHAPTER_RENDERING", "CHAPTER_STATE_CONFLICT"],
    transition:
      "Chapter approved -> inReview when the restored text differs from the Approved script",
  },
  {
    method: "POST",
    path: "/books/:id/chapters/:cid/render",
    description: "Render this one Approved Chapter after confirming its Estimate",
    request: ChapterRenderRequestSchema,
    status: [202],
    response: RenderProgressSchema,
    errors: [
      "UNAPPLIED_CHANGES",
      "CHAPTER_NOT_APPROVED",
      "CASTING_MISSING",
      "ESTIMATE_STALE",
      "VOICE_UNAVAILABLE",
      "VOICE_PRICING_UNKNOWN",
      "RERENDER_NOT_CONFIRMED",
      "CHAPTER_RENDERING",
    ],
    transition:
      "Chapter approved -> rendering -> rendered; Book -> rendering; Casting, model, and stability lock on the first Chapter Audio",
  },
  {
    method: "POST",
    path: "/books/:id/chapters/:cid/render/stop",
    description: "Stop this Chapter's render after the Block in flight",
    response: RenderProgressSchema,
    errors: ["CHAPTER_STATE_CONFLICT"],
    transition: "Chapter rendering -> approved (audio kept per Block for resume)",
  },
  {
    method: "GET",
    path: "/books/:id/chapters/:cid/audio",
    description: "302 to the Chapter Audio (mp3 or wav)",
    query: AudioQuerySchema,
    redirect: true,
  },
]);

export const castingRoutes = defineRoutes([
  {
    method: "GET",
    path: "/voices/library",
    description: "Browse the provider library without importing voices",
    query: VoiceLibraryQuerySchema,
    response: VoiceLibraryResponseSchema,
    transition: "None",
  },
  {
    method: "POST",
    path: "/books/:id/voices/import",
    description: "Import one verified library voice without changing casting",
    request: VoiceImportSchema,
    response: VoiceSchema,
    errors: ["VOICE_UNAVAILABLE", "VOICE_LANGUAGE_MISMATCH", "CASTING_LOCKED"],
    transition: "Voice becomes available; casting unchanged",
  },
  {
    method: "GET",
    path: "/voices",
    description: "Recommended and imported Voices, optionally one supported language",
    query: VoicesQuerySchema,
    response: VoicesResponseSchema,
  },
  {
    method: "GET",
    path: "/books/:id/casting",
    description: "The Narrator Voice and whether Casting is locked",
    response: CastingSchema,
  },
  {
    method: "PUT",
    path: "/books/:id/casting",
    description: "Cast the Narrator",
    request: CastingPutSchema,
    response: CastingSchema,
    errors: ["CASTING_LOCKED", "VOICE_LANGUAGE_MISMATCH"],
    transition:
      "Invalidates cached Samples' relevance, not the Samples; Estimate fingerprint unchanged",
  },
]);

export const sampleRoutes = defineRoutes([
  {
    method: "POST",
    path: "/books/:id/samples",
    description:
      "Hear a Block, a paragraph, or a term in a Voice: 200 cached, 202 rendering (poll GET /samples/:id)",
    request: SampleRequestSchema,
    status: [200, 202],
    response: SampleSchema,
    errors: ["VOICE_LANGUAGE_MISMATCH", "CASTING_MISSING"],
  },
  {
    method: "GET",
    path: "/samples/:id",
    description: "A Sample and its status",
    response: SampleSchema,
  },
  {
    method: "GET",
    path: "/samples/:id/audio",
    description: "302 to the Sample audio",
    redirect: true,
  },
]);

export const renderRoutes = defineRoutes([
  {
    method: "GET",
    path: "/books/:id/estimate",
    description:
      "Characters, credits, money per Chapter and total, the plan quota, and the fingerprint the render needs",
    response: BookEstimateSchema,
    errors: ["VOICE_UNAVAILABLE", "VOICE_PRICING_UNKNOWN"],
  },
  {
    method: "POST",
    path: "/books/:id/render",
    description:
      "Render every Approved Chapter without current audio; one job per Chapter in Chapter order",
    request: BookRenderRequestSchema,
    status: [202],
    response: RenderProgressSchema,
    errors: [
      "UNAPPLIED_CHANGES",
      "NOT_ALL_APPROVED",
      "CASTING_MISSING",
      "ESTIMATE_STALE",
      "VOICE_UNAVAILABLE",
      "VOICE_PRICING_UNKNOWN",
      "RERENDER_NOT_CONFIRMED",
      "RENDER_IN_PROGRESS",
    ],
    transition:
      "Book approved -> rendering -> rendered (after assembly); Chapters approved -> rendering -> rendered, failed after 3 attempts",
  },
  {
    method: "GET",
    path: "/books/:id/render",
    description: "Render progress: per-Chapter state, spent so far, eta",
    response: RenderProgressSchema,
  },
  {
    method: "POST",
    path: "/books/:id/render/stop",
    description:
      "Stop the Book render after the current Chapter, or at once after the Blocks in flight",
    request: StopRequestSchema,
    response: RenderProgressSchema,
    errors: ["BOOK_STATE_CONFLICT"],
    transition: "Book rendering -> stopping -> approved; queued render jobs cancelled",
  },
  {
    method: "POST",
    path: "/jobs/:id/retry",
    description: "Retry a job parked after its automatic retries (a failed Book or Chapter)",
    request: JobRetryRequestSchema,
    response: JobSummarySchema,
    errors: ["JOB_NOT_RETRYABLE", "RESPEND_NOT_CONFIRMED"],
    transition: "Book failed -> its previous state; Chapter failed -> refining or rendering",
  },
]);

export const audiobookRoutes = defineRoutes([
  {
    method: "GET",
    path: "/books/:id/audiobook",
    description: "The assembled Audiobook; 404 until the first assembly finished",
    response: AudiobookSchema,
  },
  {
    method: "GET",
    path: "/books/:id/audiobook/m4b",
    description: "302 to the M4B",
    redirect: true,
  },
  {
    method: "GET",
    path: "/books/:id/audiobook/zip",
    description: "302 to the zip of per-Chapter MP3s",
    redirect: true,
  },
]);

/** The whole API surface. Append new route groups here; never edit an existing entry in place. */
export const qualityRoutes = defineRoutes([
  {
    method: "PUT",
    path: "/books/:id/review-policy",
    description: "Choose manual or exceptions-only review",
    transition:
      "Review policy changes; existing edits and approvals remain and eligible drafts are checked",
    request: z.strictObject({ policy: ReviewPolicySchema }),
    response: BookDetailSchema,
    errors: ["UNAPPLIED_CHANGES"],
  },
  {
    method: "POST",
    path: "/books/:id/chapters/:cid/quality",
    description: "Check the current narration script",
    transition: "Enqueue a durable check for the exact current script and guidance version",
    request: z.strictObject({}),
    response: BookDetailSchema,
    errors: ["UNAPPLIED_CHANGES"],
  },
  {
    method: "POST",
    path: "/books/:id/quality/resolve",
    description: "Accept an intentional version-bound change",
    transition:
      "The exact finding is resolved; the last resolution freezes a non-calibration script",
    request: QualityResolutionSchema,
    response: BookDetailSchema,
    errors: ["UNAPPLIED_CHANGES", "QUALITY_REVIEW_REQUIRED", "CHAPTER_STATE_CONFLICT"],
  },
]);

export const routeManifest = defineRoutes([
  {
    method: "POST",
    path: "/books/:id/metadata/extract",
    description: "Extract or retry unconfirmed book details.",
    transition: "Queue bounded extraction without changing confirmed metadata",
    response: BookDetailSchema,
    errors: ["BOOK_STATE_CONFLICT"],
  },
  {
    method: "GET",
    path: "/books/:id/drafts/:key",
    description: "Read a persisted workspace draft.",
    response: DraftReadSchema,
    errors: [],
  },
  {
    method: "PUT",
    path: "/books/:id/drafts/:key",
    description: "Save a draft without activating changes.",
    transition: "Advance only the draft revision; production stays unchanged",
    request: DraftWriteSchema,
    response: DraftSchema,
    errors: ["DRAFT_CONFLICT"],
  },
  {
    method: "DELETE",
    path: "/books/:id/drafts/:key",
    description: "Discard a draft at its revision.",
    transition: "Remove the draft without changing production",
    request: DraftRevisionSchema,
    response: z.strictObject({ discarded: z.literal(true) }),
    errors: ["DRAFT_CONFLICT"],
  },
  {
    method: "POST",
    path: "/books/:id/drafts/:key/apply",
    description: "Apply a persisted draft atomically.",
    transition: "Apply validated changes through existing production gates",
    request: DraftRevisionSchema,
    response: BookDetailSchema,
    errors: [
      "DRAFT_CONFLICT",
      "BOOK_STATE_CONFLICT",
      "CHAPTER_STATE_CONFLICT",
      "CHAPTER_RENDERING",
      "CASTING_LOCKED",
      "VOICE_LANGUAGE_MISMATCH",
      "METADATA_CONFIRMATION_REQUIRED",
      "UNAPPLIED_CHANGES",
    ],
  },

  ...qualityRoutes,
  ...sessionRoutes,
  ...healthRoutes,
  ...bookRoutes,
  ...proposalRoutes,
  ...styleSheetRoutes,
  ...chapterRoutes,
  ...wordChangeRoutes,
  ...castingRoutes,
  ...sampleRoutes,
  ...renderRoutes,
  ...audiobookRoutes,
]);

export type RouteManifest = typeof routeManifest;
export type AnyRoute = RouteManifest[number];

/** "GET /books/:id": the one spelling of a route the validator, the mocks, and the tests share. */
export const routeKey = (route: Pick<RouteDef, "method" | "path">): string =>
  `${route.method} ${route.path}`;

/** The :param names of a path, as a union type: PathParams<"/books/:id/chapters/:cid"> = "id" | "cid". */
export type PathParams<P extends string> = P extends `${string}:${infer Param}/${infer Rest}`
  ? Param | PathParams<`/${Rest}`>
  : P extends `${string}:${infer Param}`
    ? Param
    : never;

/** Fills :params into a manifest path; throws on a missing or unsafe value. */
export function buildPath<P extends string>(
  path: P,
  params: Record<PathParams<P>, string>,
): string {
  return path.replace(/:([A-Za-z]+)/g, (_, name: string) => {
    const value = (params as Record<string, string | undefined>)[name];
    if (value === undefined || value === "" || value.includes("/")) {
      throw new Error(`missing or unsafe path param ${name} for ${path}`);
    }
    return encodeURIComponent(value);
  });
}

/**
 * Checks one payload against a route's response schema and reduces a failure
 * to one readable line ("chapters.0.title: expected string"). The server's
 * dev-and-test middleware and the MSW mock backend both answer through this,
 * so the drift detector is one implementation on two transports.
 */
export function checkResponse(
  route: Pick<RouteDef, "response">,
  body: unknown,
): { ok: true } | { ok: false; issues: string } {
  if (!route.response) return { ok: true };
  const parsed = route.response.safeParse(body);
  if (parsed.success) return { ok: true };
  const issues = parsed.error.issues
    .map((issue) => `${issue.path.join(".") || "(root)"}: ${issue.message}`)
    .join("; ");
  return { ok: false, issues };
}

export interface RouteMatch<R extends RouteDef = AnyRoute> {
  readonly route: R;
  readonly params: Readonly<Record<string, string>>;
}

/**
 * Finds the manifest entry for a request: method plus path relative to /api
 * (no query string). A `:param` segment matches one non-empty segment.
 */
export function matchRoute<R extends RouteDef>(
  routes: readonly R[],
  method: string,
  path: string,
): RouteMatch<R> | undefined {
  const parts = path.split("?")[0]?.split("/").filter(Boolean) ?? [];
  for (const route of routes) {
    if (route.method !== method.toUpperCase()) continue;
    const pattern = route.path.split("/").filter(Boolean);
    if (pattern.length !== parts.length) continue;
    const params: Record<string, string> = {};
    let ok = true;
    for (let i = 0; i < pattern.length; i++) {
      const p = pattern[i] as string;
      const actual = parts[i] as string;
      if (p.startsWith(":")) {
        const value = decodeParam(actual);
        if (value === undefined) {
          ok = false;
          break;
        }
        params[p.slice(1)] = value;
      } else if (p !== actual) {
        ok = false;
        break;
      }
    }
    if (ok) return { route, params };
  }
  return undefined;
}

/**
 * A path segment as a param value. A malformed percent-escape ("%E0%A4%A") is
 * no match rather than a throw, so a hand-typed URL answers 404 instead of
 * breaking the transport out from under the router.
 */
function decodeParam(segment: string): string | undefined {
  try {
    return decodeURIComponent(segment);
  } catch {
    return undefined;
  }
}
